import type { EnvironmentProviders, Provider } from '@angular/core';
import { inject, isDevMode } from '@angular/core';
import { AmlDeskTranslationService } from '@core/services/aml-desk-translation.service';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import type { Language } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import { LanguageMap } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import {
  AmlSharedTranslationService,
  localeMap,
  TRANSLATION_PROVIDER,
  TranslationService,
  UserProfileStore,
} from '@kerberos-compliance/compliance-shared-ui';
import type { NzI18nInterface } from 'ng-zorro-antd/i18n';
import { en_GB, NZ_I18N } from 'ng-zorro-antd/i18n';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './transloco-loader';

/**
 * A function that provides the appropriate Angular NZ i18n (internationalization) locale configuration
 * using the Transloco service. It retrieves the current language from Transloco and maps it to the
 * corresponding Angular NZ locale.
 *
 * @param {TranslocoService} translocoService - The Transloco service instance that handles
 * translations and language management.
 * @returns {NzI18nInterface} The Angular NZ i18n locale object corresponding to the currently active language.
 */
const provideNzI18nWithTransloco = (translocoService: TranslocoService): NzI18nInterface => {
  const currentLanguage = translocoService.getActiveLang() as Language;
  return localeMap[currentLanguage] || en_GB;
};

const provideSharedUiLibraryTranslation = (): Provider => {
  return {
    provide: AmlSharedTranslationService,
    useFactory: (appLanguageService: AmlDeskTranslationService): AmlSharedTranslationService => {
      const translationService = new AmlSharedTranslationService();
      translationService.language = appLanguageService.activeLanguage();
      appLanguageService.activeLanguage$.subscribe((language) => {
        translationService.language = language;
      });
      return translationService;
    },
    deps: [AmlDeskTranslationService],
  };
};

/**
 * Preloads the active language so translations are available during bootstrap.
 *
 * Restores the user's saved language from the persisted profile — the
 * {@link UserProfileStore} auto-hydrates from localStorage, so on a reload the
 * previously selected language is already available without re-fetching the
 * profile. This keeps the user's choice (e.g. after a Hanko reload, which does
 * not re-load the profile) instead of falling back to the hardcoded default.
 * Anonymous / first-visit sessions (no persisted profile) keep the Transloco
 * default language.
 *
 * @returns {Promise<void>} Resolves when the active language has been loaded.
 */
export const preloadActiveLanguage = async (): Promise<void> => {
  const translocoService = inject(TranslocoService);
  const translationService = inject(TranslationService);
  const userProfileStore = inject(UserProfileStore);

  const availableLangs = translocoService.getAvailableLangs() as Language[];
  const storedLang = userProfileStore.userProfile()?.language as Language | undefined;
  const activeLang =
    storedLang && availableLangs.includes(storedLang) ? storedLang : (translocoService.getActiveLang() as Language);
  translationService.setLanguage(activeLang);
  await firstValueFrom(translocoService.load(activeLang));
};

export const translationProviders: (Provider | EnvironmentProviders)[] = [
  provideTransloco({
    config: {
      availableLangs: [LanguageMap.German, LanguageMap.English],
      defaultLang: LanguageMap.German,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
    },
    loader: TranslocoHttpLoader,
  }),
  {
    provide: NZ_I18N,
    useFactory: provideNzI18nWithTransloco,
    deps: [TranslocoService],
  },
  {
    provide: TRANSLATION_PROVIDER,
    useExisting: TranslocoService,
  },
  {
    provide: TranslationService,
    useClass: AmlDeskTranslationService,
  },
  provideSharedUiLibraryTranslation(),
];
